<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import AppModal from './AppModal.vue';
import AppButton from './AppButton.vue';
import AppInput from './AppInput.vue';
import { usePrompt } from '../composables/usePrompt';

const { request, submit, cancel } = usePrompt();

const open = computed(() => request.value !== null);
const value = ref('');
const error = ref('');

watch(
  () => request.value?.id,
  () => {
    value.value = request.value?.initial ?? '';
    error.value = '';
  },
);

function validate(): string | null {
  const trimmed = value.value.trim();
  if ((request.value?.required ?? false) && trimmed === '') {
    return 'This field is required';
  }
  const custom = request.value?.validate;
  return custom !== undefined ? custom(trimmed) : null;
}

function onSubmit(): void {
  const problem = validate();
  if (problem !== null) {
    error.value = problem;
    return;
  }
  submit(value.value.trim());
}
</script>

<template>
  <AppModal :open="open" :title="request?.title ?? 'Enter a value'" @close="cancel">
    <form class="prompt" @submit.prevent="onSubmit">
      <p v-if="request?.message" class="prompt__message">{{ request.message }}</p>
      <AppInput
        v-model="value"
        :label="request?.label ?? ''"
        :type="request?.inputType ?? 'text'"
        :placeholder="request?.placeholder ?? ''"
        :error="error"
      />
    </form>
    <template #actions>
      <AppButton variant="ghost" @click="cancel">{{ request?.cancelLabel ?? 'Cancel' }}</AppButton>
      <AppButton
        :variant="request?.tone === 'danger' ? 'danger' : 'primary'"
        @click="onSubmit"
      >
        {{ request?.confirmLabel ?? 'Save' }}
      </AppButton>
    </template>
  </AppModal>
</template>

<style scoped>
.prompt__message {
  margin-bottom: var(--space-3);
  color: var(--text-secondary);
  line-height: 1.5;
}
</style>
